import { useState, useRef, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import DOMPurify from 'dompurify';
import TopBar from '../components/TopBar';
import Footer from '../components/Footer';
import RevealProvider from '../components/RevealProvider';
import EnquiryForm from '../components/EnquiryForm';
import '../styles/visit-visas.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SAFE_HTML = { ALLOWED_TAGS: ['em', 'strong', 'br', 'span', 'a'], ALLOWED_ATTR: ['href', 'target', 'rel'] };
const safe = (html) => ({ __html: DOMPurify.sanitize(html ?? '', SAFE_HTML) });

const COUNTRIES = [
  { code: 'CA', flag: '🇨🇦', name: 'Canada',        sub: 'Visitor Visa (TRV)' },
  { code: 'UK', flag: '🇬🇧', name: 'United Kingdom', sub: 'Standard Visitor Visa' },
  { code: 'AU', flag: '🇦🇺', name: 'Australia',      sub: 'Visitor Visa (subclass 600)' },
  { code: 'NZ', flag: '🇳🇿', name: 'New Zealand',    sub: 'Visitor Visa' },
  { code: 'EU', flag: '🇪🇺', name: 'Europe (Schengen)', sub: 'Short-Stay Visa (Type C)' },
];

const SCHENGEN_STATES = ['Austria','Belgium','Bulgaria','Croatia','Czechia','Denmark','Estonia','Finland','France','Germany','Greece','Hungary','Iceland','Italy','Latvia','Liechtenstein','Lithuania','Luxembourg','Malta','Netherlands','Norway','Poland','Portugal','Romania','Slovakia','Slovenia','Spain','Sweden','Switzerland'];

const ALLOWED = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
const MAX_FILES = 20;
const MAX_FILE = 4.5 * 1024 * 1024;
const MAX_TOTAL = 30 * 1024 * 1024;

function bytes(n) { return n < 1024 ? `${n} B` : n < 1024*1024 ? `${(n/1024).toFixed(1)} KB` : `${(n/1024/1024).toFixed(1)} MB`; }

function AuditTool() {
  const [step, setStep] = useState(1);
  const [country, setCountry] = useState(null);
  const [schengenState, setSchengenState] = useState('');
  const [files, setFiles] = useState([]);
  const [drag, setDrag] = useState(false);
  const [error, setError] = useState('');
  const [errorTech, setErrorTech] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  const totalBytes = files.reduce((s, f) => s + f.size, 0);
  const countryObj = COUNTRIES.find(c => c.code === country);
  const canCheck = files.length > 0 && (country !== 'EU' || schengenState);

  const addFiles = useCallback((incoming) => {
    setError(''); setErrorTech('');
    const existing = new Set(files.map(f => f.name + f.size));
    const additions = [];
    let running = totalBytes;
    for (const f of incoming) {
      if (existing.has(f.name + f.size)) continue;
      if (!ALLOWED.includes(f.type)) { setError(`${f.name}: unsupported type (use PDF, JPG, PNG, WEBP)`); continue; }
      if (f.size > MAX_FILE) { setError(`${f.name}: exceeds 4.5 MB per-file limit`); continue; }
      if (running + f.size > MAX_TOTAL) { setError(`Total upload would exceed 30 MB (current ${bytes(running + f.size)})`); break; }
      if (files.length + additions.length >= MAX_FILES) { setError(`Max ${MAX_FILES} files allowed`); break; }
      additions.push(f);
      running += f.size;
    }
    if (additions.length) setFiles(prev => [...prev, ...additions]);
  }, [files, totalBytes]);

  const onDrop = (e) => { e.preventDefault(); setDrag(false); addFiles(Array.from(e.dataTransfer.files || [])); };
  const onSelect = (e) => addFiles(Array.from(e.target.files || []));
  const removeFile = (i) => setFiles(files.filter((_, idx) => idx !== i));

  const submit = async () => {
    setLoading(true); setError(''); setErrorTech('');
    try {
      const fd = new FormData();
      fd.append('country', country);
      if (country === 'EU') fd.append('schengen_state', schengenState);
      files.forEach(f => fd.append('files', f));
      const { data } = await axios.post(`${API}/visa-audit/assess`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      setResult(data); setStep(3);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Unknown error';
      const status = e?.response?.status;
      let msg = 'The assessment came back incomplete. Please retry.';
      if (!e?.response) msg = 'The request never reached the assessment service. Try one small file first.';
      else if (status === 413) msg = 'Your files are too large in total. Remove or compress the biggest files.';
      else if (status === 503 || status === 429) msg = 'The service is busy. Wait a moment and press retry.';
      else if (status === 400) msg = detail;
      setError(msg); setErrorTech(`${status || 'network'} — ${detail}`);
    } finally { setLoading(false); }
  };

  const reset = () => { setStep(1); setCountry(null); setSchengenState(''); setFiles([]); setResult(null); setError(''); setErrorTech(''); };
  const backToUpload = () => { setStep(2); setResult(null); setError(''); setErrorTech(''); };

  const band = result ? (result.score >= 75 ? 'g' : result.score >= 50 ? 'a' : 'r') : 'g';

  return (
    <div className="vv-audit" id="audit-tool" data-testid="vv-audit-tool">
      {loading && (
        <div className="vv-loading" data-testid="vv-loading">
          <div className="vv-loading-dots"><span/><span/><span/></div>
          <div className="vv-loading-label">Reviewing {files.length} document{files.length===1?'':'s'} for {countryObj?.name}…</div>
          <div className="vv-loading-sub">Comparing against the standard {countryObj?.sub} checklist. This can take up to a minute.</div>
        </div>
      )}

      {!loading && step === 1 && (
        <>
          <div className="vv-step-marker">Step 1 / 3 — Destination</div>
          <div className="vv-country-grid" data-testid="vv-country-grid">
            {COUNTRIES.map(c => (
              <button key={c.code} type="button"
                className={`vv-country-btn ${country === c.code ? 'active' : ''}`}
                onClick={() => setCountry(c.code)}
                data-testid={`vv-country-${c.code}`}>
                <div className="vv-country-flag" aria-hidden="true">{c.flag}</div>
                <div className="vv-country-name">{c.name}</div>
                <div className="vv-country-sub">{c.sub}</div>
              </button>
            ))}
          </div>
          {country === 'EU' && (
            <div className="vv-schengen-select">
              <label htmlFor="vv-sch">Main destination country</label>
              <select id="vv-sch" value={schengenState} onChange={e => setSchengenState(e.target.value)} data-testid="vv-schengen-state">
                <option value="">Select destination country first…</option>
                {SCHENGEN_STATES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          )}
          <div className="vv-actions-row">
            <button className="vv-btn-primary" disabled={!country || (country === 'EU' && !schengenState)} onClick={() => setStep(2)} data-testid="vv-step1-next">
              Continue to documents →
            </button>
          </div>
        </>
      )}

      {!loading && step === 2 && (
        <>
          <div className="vv-step-marker">Step 2 / 3 — Documents · {countryObj?.name}</div>
          <div className={`vv-dropzone ${drag ? 'drag' : ''}`}
            onDragOver={e => { e.preventDefault(); setDrag(true); }}
            onDragLeave={() => setDrag(false)}
            onDrop={onDrop}
            onClick={() => inputRef.current?.click()}
            data-testid="vv-dropzone">
            <div className="vv-dropzone-icon">⬆</div>
            <div><strong>Drop your documents here</strong> or tap to browse</div>
            <div className="vv-dropzone-hint">
              PDF, JPG, PNG, WEBP · max {MAX_FILES} files · 4.5 MB each · 30 MB total<br/>
              Upload: passport, bank statements, employment / business proof, itinerary, bookings, photos, forms, invitation letter, insurance
            </div>
            <input ref={inputRef} type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/*"
              style={{ display: 'none' }} onChange={onSelect} data-testid="vv-file-input" />
          </div>
          {files.length > 0 && (
            <>
              <div className="vv-file-list">
                {files.map((f, i) => (
                  <div key={i} className="vv-file-row">
                    <div className="vv-file-name">{f.name}</div>
                    <div className="vv-file-size">{bytes(f.size)}</div>
                    <button className="vv-file-remove" onClick={() => removeFile(i)} aria-label={`Remove ${f.name}`}>×</button>
                  </div>
                ))}
              </div>
              <div className="vv-file-summary">{files.length}/{MAX_FILES} files · {bytes(totalBytes)}/30.0 MB</div>
            </>
          )}
          {error && <div className="vv-error" data-testid="vv-error">{error}{errorTech && <div className="vv-error-tech">{errorTech}</div>}</div>}
          <div className="vv-actions-row">
            <button className="vv-btn-primary" disabled={!canCheck} onClick={submit} data-testid="vv-step2-submit">
              Check my readiness ({files.length} file{files.length===1?'':'s'})
            </button>
            <button className="vv-btn-ghost" onClick={() => setStep(1)}>← Back to destination</button>
          </div>
        </>
      )}

      {!loading && step === 3 && result && (
        <div data-testid="vv-result">
          <div className="vv-step-marker">Step 3 / 3 — Your readiness</div>
          <div className="vv-result-head">
            <div className={`vv-score-stamp ${band}`} data-testid="vv-score">
              <div className="vv-score-val">{result.score}%</div>
              <div className="vv-score-label">READY SCORE</div>
            </div>
            <div className="vv-verdict">
              <div className="vv-verdict-txt">{result.verdict}</div>
              <div className="vv-verdict-dis">Estimated documentation readiness — not an official decision or guarantee. Final outcomes rest with the immigration authority.</div>
            </div>
          </div>
          <div className="vv-checklist">
            {result.documents.map((d, i) => (
              <div key={i} className="vv-check-item">
                <div className={`vv-check-pill ${d.status}`}>
                  {d.status === 'present' ? 'Present' : d.status === 'issue' ? 'Fix needed' : 'Missing'}
                </div>
                <div className="vv-check-body">
                  <div className="vv-check-name">{d.name}</div>
                  {d.note && <div className="vv-check-note">{d.note}</div>}
                </div>
              </div>
            ))}
          </div>
          {result.actions?.length > 0 && (
            <div className="vv-actions-list">
              <h3>What to fix first</h3>
              <ol>
                {result.actions.map((a, i) => <li key={i} data-n={String(i + 1).padStart(2, '0')}>{a}</li>)}
              </ol>
            </div>
          )}
          <div className="vv-actions-row">
            <button className="vv-btn-ghost" onClick={backToUpload}>← Add or replace documents</button>
            <button className="vv-btn-ghost" onClick={reset}>Start a new check</button>
          </div>
          <div className="vv-footer-dis">Visa Ready Score reviews documentation completeness only. It does not create, edit, or submit documents, and it is not immigration advice. Your uploads are analysed and immediately discarded — nothing is stored.</div>
        </div>
      )}
    </div>
  );
}

const QUESTIONS = [
  { q: 'Where did this money come from?',
    a: 'Balance is not the test. <strong>Provenance is.</strong> A large deposit that lands three weeks before you apply reads as borrowed funds, and borrowed funds read as a trip you cannot actually afford. Six months of movement tells a truer story than one healthy closing figure.' },
  { q: 'Why would you come back?',
    a: 'This is the question that decides most cases. Employment, property, dependants, a business, a lease, an ongoing course — <strong>ties are evidence, not assertions.</strong> Saying you will return is not the same as showing what you would be leaving behind.' },
  { q: 'Does this trip make sense?',
    a: 'A fortnight booked in a city you have given no reason to visit, funded at a level that doesn\u2019t match your stated income, over dates that overlap your working month. <strong>Internal coherence is assessed even when nothing is technically missing.</strong>' },
  { q: 'Who is really paying for this?',
    a: 'If someone else is funding the trip, their finances and their status are now part of your application. <strong>An undeclared sponsor is worse than a modest one.</strong> Half-disclosed support invites the officer to assume the rest.' },
  { q: 'What happened the last time?',
    a: 'A previous refusal — from any country, at any time — is not fatal. <strong>Failing to disclose it very often is.</strong> Immigration authorities share data, and an omission turns a survivable history into a credibility problem.' },
  { q: 'Do these documents agree with each other?',
    a: 'The salary on the employment letter against the credits in the statement. The leave dates against the flights. The address on the ID against the one on the form. <strong>Most files are refused on contradiction, not absence.</strong>' },
];

const COUNTRY_TIPS = [
  { name: 'India',                 tag: 'Applying from home', note: '<strong>ITR-V for three years.</strong> The most-checked document in an Indian file, and the one most often absent.' },
  { name: 'United Arab Emirates',  tag: 'Residents',          note: '<strong>Employer no-objection certificate.</strong> Expected as standard, and rarely included unless someone tells you to get it.' },
  { name: 'Nigeria',               tag: 'Applying from home', note: '<strong>Six-month bank statement stamped by the branch.</strong> App PDFs alone rarely satisfy the credibility test.' },
  { name: 'Philippines',           tag: 'Applying from home', note: '<strong>Certificate of employment with salary and leave dates.</strong> Must be on letterhead, dated within the last 30 days.' },
  { name: 'Pakistan',              tag: 'Applying from home', note: '<strong>Property or business ownership evidence.</strong> The single strongest tie-to-home document for Pakistani applicants.' },
  { name: 'Saudi Arabia / Qatar',  tag: 'Residents',          note: '<strong>Iqama / QID + employer no-objection letter.</strong> Missing either signals a weak residency claim.' },
];

export default function VisitVisas() {
  const url = 'https://nomadcompasshub.com/visit-visas';
  return (
    <RevealProvider>
      <Helmet>
        <title>Visit Visa Audit — Nomad Compass</title>
        <meta name="description" content="Upload the documents you plan to submit for your Canada, UK, Australia, New Zealand or Schengen visitor visa. Get a readiness score and see what an officer will find missing — before you apply." />
        <link rel="canonical" href={url} />
        <meta property="og:title" content="Visit Visa Audit — Nomad Compass" />
        <meta property="og:description" content="Upload your visa documents. We show you what's missing, unclear, or contradictory — before you apply." />
        <meta property="og:url" content={url} />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      <TopBar variant="page" />

      <section className="vv-hero" aria-labelledby="vv-hero-title">
        <div className="vv-hero-overlay" aria-hidden="true" />
        <div className="vv-hero-inner">
          <div className="vv-hero-left reveal">
            <div className="vv-hero-eyebrow">Visitor visas · Canada · UK · Australia · New Zealand · Schengen</div>
            <h1 className="vv-hero-title" id="vv-hero-title">
              Travel Should Feel Like Adventure. <em>Not Homework.</em>
            </h1>
            <p className="vv-hero-lead">
              While you&rsquo;re busy picking destinations, we&rsquo;re decoding embassy requirements, chasing documents, and triple-checking every form. You pack the bags. We handle the bureaucracy.
            </p>
            <div className="vv-hero-ctas">
              <a href="#enquiry" className="vv-btn-primary" data-testid="vv-hero-cta">Start Your Application</a>
              <a href="#audit-tool" style={{ color: 'var(--brass-deep)', textDecoration: 'underline', textUnderlineOffset: 6, fontSize: 14, fontWeight: 500 }}>Or audit your file first →</a>
            </div>
          </div>
        </div>
      </section>

      <section className="vv-section" id="audit">
        <div className="vv-eyebrow-center reveal"><span className="line"/><span className="text">Visit visa audit</span><span className="line"/></div>
        <h2 className="vv-h2 reveal" style={{ textAlign: 'center' }}>
          Find out your Visa Score. <em>See if you missed something in your application.</em>
        </h2>
        <p className="reveal" style={{ margin: '22px auto 0', maxWidth: '44em', textAlign: 'center', fontSize: 16.5, lineHeight: 1.78, color: 'var(--warm-grey)', fontWeight: 300 }}>
          We do not predict whether you will be approved. Nobody honestly can. We tell you what an officer will find when they open your file — every item present, needing work, or missing, and what to fix first.
        </p>
        <AuditTool />
      </section>

      <section className="vv-section" id="assessed">
        <div className="vv-eyebrow-center reveal"><span className="line"/><span className="text">What is actually assessed</span><span className="line"/></div>
        <h2 className="vv-h2 reveal" style={{ textAlign: 'center' }}>
          An officer is not reading your documents. <em>They are asking six questions.</em>
        </h2>
        <div className="vv-questions">
          {QUESTIONS.map((q, i) => (
            <div key={i} className="vv-question reveal">
              <h3>{q.q}</h3>
              <p dangerouslySetInnerHTML={safe(q.a)} />
            </div>
          ))}
        </div>
      </section>

      <section className="vv-section" id="countries">
        <div className="vv-eyebrow-center reveal"><span className="line"/><span className="text">Country of application</span><span className="line"/></div>
        <h2 className="vv-h2 reveal" style={{ textAlign: 'center' }}>
          Your documents depend on where you are applying from. <em>Not the passport you hold.</em>
        </h2>
        <p className="reveal" style={{ margin: '22px auto 0', maxWidth: '52em', textAlign: 'center', fontSize: 16.5, lineHeight: 1.78, color: 'var(--warm-grey)', fontWeight: 300 }}>
          An Indian citizen applying from Dubai does not submit Indian paperwork. Get this wrong and the file is incomplete before anyone has read it. Here is the document most often missing from each.
        </p>
        <div className="vv-country-cards">
          {COUNTRY_TIPS.map(c => (
            <article key={c.name} className="vv-country-card reveal">
              <h3>{c.name}</h3>
              <div className="tag"><span>{c.tag}</span></div>
              <p dangerouslySetInnerHTML={safe(c.note)} />
            </article>
          ))}
        </div>
      </section>

      <section className="vv-section" id="enquiry">
        <div className="vv-eyebrow-center reveal"><span className="line"/><span className="text">Prefer a human on your file?</span><span className="line"/></div>
        <h2 className="vv-h2 reveal" style={{ textAlign: 'center' }}>
          The audit tool tells you what to fix. <em>We can fix it with you.</em>
        </h2>
        <div style={{ maxWidth: 640, margin: '32px auto 0' }}>
          <EnquiryForm pageSource="visit-visas" variant="country" defaultCountry="Visit Visa" visitorMode />
        </div>
      </section>

      <Footer />
    </RevealProvider>
  );
}
